import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import RecentlyViewedProducts from "./recently-viewed-products";
import ProductReviews from "./product-reviews";
import { useAuth } from "@/context/Auth";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addToCart as addToCartAction } from "@/store/slices/cartSlice";
import {
  fetchProductById,
  clearCurrentProduct,
  selectCurrentProduct,
  selectCurrentProductLoading,
  selectCurrentProductVariations,
  selectCurrentProductReviews,
} from "@/store/slices/productSlice";
import axios from "@/utils/axios";
import { ProductDetailsSkeleton } from "./skeletons";
import NotFound from "../404/404";
import { formatCurrency } from "@/utils/format";
import Seo from "@/seo/seo";
import { getCanonicalUrl } from "@/utils/seo-config";

export default function ProductDetailsPage() {
  const { productId } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState("");
  const [selectedVariation, setSelectedVariation] = useState(null);
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  
  // Redux selectors
  const product = useAppSelector(selectCurrentProduct);
  const loading = useAppSelector(selectCurrentProductLoading);
  const variations = useAppSelector(selectCurrentProductVariations);
  const reviews = useAppSelector(selectCurrentProductReviews);

  // ✅ Fetch product by ID
  useEffect(() => {
    if (productId) {
      dispatch(fetchProductById(productId));
    }
    
    // Clear product when component unmounts
    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [dispatch, productId]);

  // Set active image when product loads
  useEffect(() => {
    if (product) {
      setActiveImage(product.images?.[0] || product.image);
      handleViewed(product); // ✅ track recently viewed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  // Clear selection if variations are removed
  useEffect(() => {
    if ((!variations || variations.length === 0) && selectedVariation) {
      // Clear selection if no variations
      setSelectedVariation(null);
    }
  }, [variations, selectedVariation]);

  const handleViewed = async (product) => {
    try {
      if (user) {
        await axios.post("/products/recently-viewed", {
          productId: product.id, // ✅ always normalized
        });
      } else {
        const existing =
          JSON.parse(localStorage.getItem("recentlyViewed")) || [];
        const updated = [
          product,
          ...existing.filter((p) => p.id !== product.id),
        ].slice(0, 10);
        localStorage.setItem("recentlyViewed", JSON.stringify(updated));
      }
    } catch (err) {
      console.error("Error saving recently viewed:", err);
    }
  };

  // Get current price, stock, and SKU based on selected variation or base product
  const currentPrice = useMemo(() => {
    if (selectedVariation) {
      const variationPrice = Number(selectedVariation.price || 0);
      // Handle both string and number discount values
      const variationDiscountValue = selectedVariation.discount || product?.discount || 0;
      const variationDiscount = Number(variationDiscountValue) || 0;
      if (variationDiscount > 0) {
        return variationPrice - (variationPrice * variationDiscount) / 100;
      }
      return variationPrice;
    }
    // Base product price - handle string discounts
    const discountValue = product?.discount || 0;
    const discount = Number(discountValue) || 0;
    const hasDiscount = discount > 0;
    return hasDiscount
      ? Number(product.price || 0) - (Number(product.price || 0) * discount) / 100
      : Number(product?.price || 0);
  }, [selectedVariation, product]);

  const currentStock = useMemo(() => {
    if (selectedVariation) {
      return Number(selectedVariation.stock_quantity || 0);
    }
    return Number(product?.stock_quantity || 0);
  }, [selectedVariation, product]);

  const currentSku = useMemo(() => {
    if (selectedVariation?.sku) {
      return selectedVariation.sku;
    }
    return product?.sku || product?.productId || product?.id;
  }, [selectedVariation, product]);

  const handleAddToCart = () => {
    if (!product) return;

    // Use variation data if selected, otherwise use base product
    const productData = selectedVariation
      ? {
          ...product,
          id: product.id ?? product.productId,
          productId: product.productId || product.id,
          price: currentPrice,
          stock_quantity: currentStock,
          sku: currentSku,
          image: product.image,
          variationId: selectedVariation.id, // Include variation ID
          variation: {
            id: selectedVariation.id,
            size: selectedVariation.size,
            scent: selectedVariation.scent,
            color: selectedVariation.color,
            variant_name: selectedVariation.variant_name,
            price: selectedVariation.price,
            discount: selectedVariation.discount,
            stock_quantity: selectedVariation.stock_quantity,
            sku: selectedVariation.sku,
          },
        }
      : {
          ...product,
          id: product.id ?? product.productId,
          price: currentPrice,
          image: product.image,
        };

    dispatch(
      addToCartAction({
        product: productData,
        quantity,
        user,
      })
    );
  };

  const handleQuantityChange = (e) => {
    const value = Number.parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setQuantity(value);
    } else if (e.target.value === "") {
      setQuantity("");
    }
  };

  const incrementQuantity = () => {
    setQuantity((prev) => {
      const newQty = typeof prev === "number" ? prev + 1 : 1;
      // Don't allow quantity to exceed stock
      if (currentStock > 0 && newQty > currentStock) {
        return currentStock;
      }
      return newQty;
    });
  };

  const decrementQuantity = () => {
    setQuantity((prev) =>
      typeof prev === "number" && prev > 1 ? prev - 1 : 1
    );
  };

  // Get unique variation attributes for display
  const variationAttributes = useMemo(() => {
    if (!variations || variations.length === 0) return { sizes: [], scents: [], colors: [] };
    
    const sizes = [...new Set(variations.map(v => v.size).filter(Boolean))];
    const scents = [...new Set(variations.map(v => v.scent).filter(Boolean))];
    const colors = [...new Set(variations.map(v => v.color).filter(Boolean))];
    
    return { sizes, scents, colors };
  }, [variations]);

  // Calculate final price for SEO (use base product or first variation)
  const finalPrice = useMemo(() => {
    if (variations && variations.length > 0) {
      const firstVar = variations[0];
      const varPrice = Number(firstVar.price || 0);
      // Handle both string and number discount values
      const varDiscountValue = firstVar.discount || product?.discount || 0;
      const varDiscount = Number(varDiscountValue) || 0;
      if (varDiscount > 0) {
        return varPrice - (varPrice * varDiscount) / 100;
      }
      return varPrice;
    }
    // Handle string discounts for base product
    const discountValue = product?.discount || 0;
    const discount = Number(discountValue) || 0;
    const hasDiscount = discount > 0;
    return hasDiscount
      ? Number(product?.price || 0) - (Number(product?.price || 0) * discount) / 100
      : Number(product?.price || 0);
  }, [product, variations]);

  // Filter variations based on selected attributes
  const getAvailableVariations = (size = null, scent = null, color = null) => {
    if (!variations) return [];
    return variations.filter(v => {
      if (v.is_active === 0) return false;
      if (size && v.size !== size) return false;
      if (scent && v.scent !== scent) return false;
      if (color && v.color !== color) return false;
      return true;
    });
  };

  // Handle variation selection by attribute
  const handleVariationSelect = (attribute, value) => {
    const currentSize = attribute === 'size' ? value : (selectedVariation?.size || null);
    const currentScent = attribute === 'scent' ? value : (selectedVariation?.scent || null);
    const currentColor = attribute === 'color' ? value : (selectedVariation?.color || null);
    
    const available = getAvailableVariations(currentSize, currentScent, currentColor);
    if (available.length > 0) {
      setSelectedVariation(available[0]);
      // Reset quantity if it exceeds new stock
      if (quantity > available[0].stock_quantity) {
        setQuantity(1);
      }
    }
  };

  if (loading) {
    return <ProductDetailsSkeleton />;
  }

  if (!product) {
    return (
      <NotFound />
    );
  }

  return (
    <>
      <Seo
        type="product"
        title={product.name}
        description={product.description || `Shop ${product.name} - Premium organic skincare product. ${product.key_benefits ? `Key benefits: ${product.key_benefits}` : ''}`}
        image={product.image || (product.images && product.images[0])}
        url={getCanonicalUrl(`/products/${product.productId || product.id}`)}
        canonical={getCanonicalUrl(`/products/${product.productId || product.id}`)}
        keywords={`${product.name}, organic skincare, ${product.category || 'skincare'}, natural beauty`}
        product={{
          name: product.name,
          description: product.description,
          image: product.image,
          images: product.images || [product.image],
          sku: currentSku || product.sku || product.productId || product.id,
          price: finalPrice,
          currency: "NGN",
          brand: "Fayoz",
          availability: currentStock > 0 ? "InStock" : "OutOfStock",
          stock_quantity: currentStock,
          rating: product.rating,
          reviewCount: product.reviews,
          category: product.category,
        }}
      />
      <div className="container mx-auto py-8 px-4 md:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Image Gallery */}
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md aspect-square overflow-hidden rounded-lg border">
              <img
                src={activeImage || product.image || "/placeholder.svg"}
                alt={product.name}
                width={600}
                height={600}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = "/placeholder.svg";
                }}
              />
            </div>
            <div className="flex gap-2 mt-4">
              {product.images?.map((img, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "w-16 h-16 rounded-md overflow-hidden border",
                    activeImage === img && "border-primary ring-2 ring-primary"
                  )}
                  onClick={() => setActiveImage(img)}
                >
                  <img
                    src={img || "/placeholder.svg"}
                    alt={`${product.name} thumbnail ${index + 1}`}
                    width={64}
                    height={64}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "/placeholder.svg";
                    }}
                  />
                </Button>
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div className="flex flex-col gap-6">
            <div>
              <h1 className="text-3xl font-bold">{product.name}</h1>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon
                      key={i}
                      className={cn(
                        "w-5 h-5",
                        i < Math.floor(product.rating || 0)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      )}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-500">
                  ({product.reviews || 0} reviews)
                </span>
              </div>

              {/* Price + Discount */}
              <div className="flex flex-col gap-2 mt-4">
                <div className="flex items-baseline gap-2 flex-wrap">
                  {(() => {
                    const basePrice = selectedVariation 
                      ? Number(selectedVariation.price || 0)
                      : Number(product.price || 0);
                    // Handle both string and number discount values
                    const discountValue = selectedVariation
                      ? (selectedVariation.discount || product.discount || 0)
                      : (product.discount || 0);
                    const discount = Number(discountValue) || 0;
                    const finalPrice = currentPrice;
                    // Check discount same way as product-showcase.jsx
                    const hasDiscount = discount > 0;
                    
                    if (hasDiscount && basePrice > 0) {
                      return (
                        <>
                          <p className="text-4xl font-bold text-primary">
                            {formatCurrency(finalPrice)}
                          </p>
                          <span className="text-lg text-gray-500 line-through">
                            {formatCurrency(basePrice)}
                          </span>
                          <span className="text-sm font-semibold text-green-600 bg-green-100 px-2 py-1 rounded">
                            {discount}% OFF
                          </span>
                        </>
                      );
                    }
                    return (
                      <p className="text-4xl font-bold text-primary">
                        {formatCurrency(finalPrice)}
                      </p>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Variation Selectors */}
            {variations && variations.length > 0 && (
              <div className="space-y-4">
                {!selectedVariation && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">Note:</span> This product has variations available. 
                      Select a variation below to see specific pricing and availability, or you can add the base product to cart.
                    </p>
                  </div>
                )}
                {/* Size Selector */}
                {variationAttributes.sizes.length > 0 && (
                  <div className="grid gap-2">
                    <h3 className="font-semibold text-lg">Size:</h3>
                    <div className="flex flex-wrap gap-2">
                      {variationAttributes.sizes.map((size) => {
                        const available = getAvailableVariations(size, selectedVariation?.scent, selectedVariation?.color);
                        const isSelected = selectedVariation?.size === size;
                        const isAvailable = available.length > 0;
                        
                        return (
                          <Button
                            key={size}
                            type="button"
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleVariationSelect('size', size)}
                            disabled={!isAvailable}
                            className={cn(
                              isSelected && "ring-2 ring-primary",
                              !isAvailable && "opacity-50 cursor-not-allowed"
                            )}
                          >
                            {size}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Scent Selector */}
                {variationAttributes.scents.length > 0 && (
                  <div className="grid gap-2">
                    <h3 className="font-semibold text-lg">Scent:</h3>
                    <div className="flex flex-wrap gap-2">
                      {variationAttributes.scents.map((scent) => {
                        const available = getAvailableVariations(selectedVariation?.size, scent, selectedVariation?.color);
                        const isSelected = selectedVariation?.scent === scent;
                        const isAvailable = available.length > 0;
                        
                        return (
                          <Button
                            key={scent}
                            type="button"
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleVariationSelect('scent', scent)}
                            disabled={!isAvailable}
                            className={cn(
                              isSelected && "ring-2 ring-primary",
                              !isAvailable && "opacity-50 cursor-not-allowed"
                            )}
                          >
                            {scent}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Color Selector */}
                {variationAttributes.colors.length > 0 && (
                  <div className="grid gap-2">
                    <h3 className="font-semibold text-lg">Color:</h3>
                    <div className="flex flex-wrap gap-2">
                      {variationAttributes.colors.map((color) => {
                        const available = getAvailableVariations(selectedVariation?.size, selectedVariation?.scent, color);
                        const isSelected = selectedVariation?.color === color;
                        const isAvailable = available.length > 0;
                        
                        return (
                          <Button
                            key={color}
                            type="button"
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleVariationSelect('color', color)}
                            disabled={!isAvailable}
                            className={cn(
                              isSelected && "ring-2 ring-primary",
                              !isAvailable && "opacity-50 cursor-not-allowed"
                            )}
                          >
                            {color}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ✅ Description */}
            {product.description && (
              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>
            )}

            {/* ✅ Key Benefits */}
            {product.key_benefits && (
              <div className="grid gap-2">
                <h3 className="font-semibold text-lg">Key Benefits:</h3>
                <ul className="list-disc list-inside text-gray-600">
                  {product.key_benefits.split(",").map((benefit, index) => (
                    <li key={index}>{benefit.trim()}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* ✅ Ingredients */}
            {product.ingredients && (
              <div className="grid gap-2">
                <h3 className="font-semibold text-lg">Ingredients:</h3>
                <p className="text-gray-600">{product.ingredients}</p>
              </div>
            )}

            {/* ✅ Skin Type */}
            {product.skin_type && (
              <div className="grid gap-2">
                <h3 className="font-semibold text-lg">Skin Type:</h3>
                <p className="text-gray-600">{product.skin_type}</p>
              </div>
            )}

            {/* ✅ Usage Instructions */}
            {product.usage_instructions && (
              <div className="grid gap-2">
                <h3 className="font-semibold text-lg">How to Use:</h3>
                <p className="text-gray-600">{product.usage_instructions}</p>
              </div>
            )}

            {/* ✅ Warnings */}
            {product.warnings && (
              <div className="grid gap-2">
                <h3 className="font-semibold text-lg text-red-600">
                  Warnings:
                </h3>
                <p className="text-gray-600">{product.warnings}</p>
              </div>
            )}

            {/* ✅ Stock & SKU */}
            <div className="grid gap-2">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">Stock:</span>{" "}
                {currentStock > 0 ? (
                  <span className="text-green-600">
                    {currentStock} available
                  </span>
                ) : (
                  <span className="text-red-600">Out of stock</span>
                )}
              </p>
              {currentSku && (
                <p className="text-sm text-gray-500">
                  <span className="font-semibold">SKU:</span> {currentSku}
                </p>
              )}
            </div>

            {/* ✅ Quantity + Add to Cart */}
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center border rounded-md">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={decrementQuantity}
                  disabled={quantity <= 1}
                >
                  -
                </Button>
                <Input
                  type="number"
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="w-16 text-center border-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  min="1"
                  max={currentStock > 0 ? currentStock : undefined}
                  aria-label="Product quantity"
                />
                <Button variant="ghost" size="icon" onClick={incrementQuantity}>
                  +
                </Button>
              </div>
              <Button
                size="lg"
                className="flex-1"
                onClick={handleAddToCart}
                disabled={currentStock <= 0}
              >
                {currentStock <= 0 
                  ? "Out of Stock" 
                  : "Add to Cart"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Reviews Section */}
      {user && <ProductReviews productId={productId} />}

      {/* Recently Viewed */}
      <RecentlyViewedProducts />
    </>
  );
}
